import catchCancel from 'helpers/catchCancel';
import { calculateAppliedArea } from 'helpers/calculateAppliedArea';
import { products as productsApi, product as productApi } from 'utilities/api';
import useUomConvert from 'hooks/useUomConvert';

const useMasterProducts = () => {
  const convertFn = useUomConvert();

  // Fetches a product's std conversion metadata from the master record and
  // returns the hydrated product plus `fetched`. When the request fails or is
  // cancelled we report `fetched: false` and return the product UNCHANGED — we
  // deliberately do not invent approximate fallback values, so accuracy-
  // sensitive callers can surface an error instead of recomputing from a guess.
  const fetchStdUnits = async product => {
    const { promise } =
      product.custom === true
        ? productApi.fetch(product.productId)
        : productsApi.fetch(product.productId);
    // A cancelled request resolves to undefined (catchCancel swallows it
    // without rethrowing); a real failure resolves to null.
    const response = await promise.catch(catchCancel).catch(() => null);
    if (!response?.data) {
      return { product, fetched: false };
    }

    const { data } = response;
    if (data.stdFactor === 0) {
      data.stdFactor = 1;
    } else if (data.stdfactor === 0) {
      data.stdfactor = 1;
    }

    const hydratedProduct = {
      ...product,
      // Keep the existing density when the response omits it, so hydration
      // never clears a value the product already had (which would break later
      // mass/volume conversions). `??` keeps a legitimate 0 from the response.
      density: data.density ?? product.density,
      // Custom products come from a different endpoint that returns camelCase
      // keys; the master-data endpoint returns lowercase.
      stdfactor: product.custom === true ? data.stdFactor : data.stdfactor,
      stdunit:
        product.custom === true
          ? data.standardUnit
          : data.stdunit || data.stdUnit,
      stdpackageunit:
        product.custom === true ? data.stdPackageUnit : data.stdpackageunit
    };

    return { product: hydratedProduct, fetched: true };
  };

  const getStdUnits = async product => {
    const { product: hydrated, fetched } = await fetchStdUnits(product);
    if (fetched) {
      return hydrated;
    }
    // Back-compat for existing consumers (Tasks) that expect product-local
    // fallback values when the master record can't be fetched.
    return {
      ...product,
      stdfactor: product.productRateValue === 0 ? 1 : product.productRateValue,
      stdunit: product.productRateUnit,
      stdpackageunit: product.specificCostUnit
    };
  };

  const appliedAreaCalculation = async (product, unmaskedAreaValue) => {
    const { stdfactor, stdunit, stdpackageunit } = product;
    let mappedProduct = product;

    if (!stdfactor || !stdunit || !stdpackageunit) {
      mappedProduct = await getStdUnits(product);
    }

    return calculateAppliedArea(mappedProduct, unmaskedAreaValue, convertFn);
  };

  return { appliedAreaCalculation, getStdUnits, fetchStdUnits };
};

export default useMasterProducts;
